import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import en from './locales/en.json';
import tr from './locales/tr.json';
import { BackendErrorData } from '../types/error';
import {
  DEFAULT_LANGUAGE,
  ENABLED_LANGUAGES,
  LanguageCode,
  LanguageInfo,
  isEnabledLanguage,
  languageInfo,
  matchLanguage,
} from './languages';

export type Language = LanguageCode;
export type { LanguageInfo };
export { LANGUAGES, ENABLED_LANGUAGES, languageInfo } from './languages';

type TranslateParams = { [key: string]: string | number };

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: TranslateParams) => string;
  languages: LanguageInfo[];
}

// localStorage throws (SecurityError) when the browser blocks site storage
const readSavedLanguage = (): Language | null => {
  try {
    const saved = localStorage.getItem('language');
    return isEnabledLanguage(saved) ? saved : null;
  } catch {
    return null;
  }
};

const browserLanguage = (): Language => {
  try {
    return matchLanguage(navigator.language) ?? DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

const saveLanguage = (lang: Language) => {
  try {
    localStorage.setItem('language', lang);
  } catch {}
};

// A link from riselio.com into the flow says the site's language (#/start?lang=de, T-165)
const linkLanguage = (): Language | null => {
  try {
    const hash = window.location.hash;
    const at = hash.indexOf('?');
    if (at < 0 || !/^#\/start(\/|\?|$)/.test(hash)) return null;
    const lang = new URLSearchParams(hash.slice(at + 1)).get('lang');
    return isEnabledLanguage(lang) ? lang : null;
  } catch {
    return null;
  }
};

/** A first visit's language: the link's (the site the visitor read), else the browser's. */
const unsavedLanguage = (): Language => linkLanguage() ?? browserLanguage();

// First visit: follow the link or the browser and save it, so the Accept-Language header sent
// by apiInterceptors matches the UI from the very first request. A saved choice always wins.
const initialLanguage = (): Language => {
  const saved = readSavedLanguage();
  if (saved) return saved;
  const detected = unsavedLanguage();
  saveLanguage(detected);
  return detected;
};

// en and tr ship in the bundle, so switching between them is synchronous; other languages are
// fetched as separate chunks the first time they are selected
i18n
  .use(resourcesToBackend((lng: string) => import(`./locales/${lng}.json`)))
  .init({
    resources: { en: { translation: en }, tr: { translation: tr } },
    partialBundledLanguages: true,
    lng: readSavedLanguage() ?? unsavedLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: ENABLED_LANGUAGES.map((lang) => lang.code),
    load: 'languageOnly',
    initAsync: false,
    // Keys are dotted paths; backend error texts may contain ':' so namespaces are off
    nsSeparator: false,
    returnNull: false,
    interpolation: { prefix: '{', suffix: '}', escapeValue: false },
    // Leave an unfilled placeholder visible instead of dropping it
    missingInterpolationHandler: (_text: string, match: RegExpExecArray) => match[0],
  });

/** The UI language outside React (saved choice, else the link's or the browser's). */
export const currentLanguage = (): Language => readSavedLanguage() ?? unsavedLanguage();

/** Resolves once the starting language's catalog is loaded (instant for the bundled en/tr). */
export const i18nReady: Promise<unknown> = i18n.loadLanguages(currentLanguage()).catch(() => undefined);

const applyDocumentLanguage = (lang: Language) => {
  try {
    document.documentElement.lang = lang;
    document.documentElement.dir = languageInfo(lang).dir;
  } catch {}
};

const translate = (language: Language, key: string, params?: TranslateParams): string =>
  i18n.t(key, { ...params, lng: language }) as string;

/** A string array from the catalog (e.g. inspiration.quotes); empty when the key is missing. */
export const translateList = (language: Language, key: string): string[] => {
  const value = i18n.t(key, { lng: language, returnObjects: true });
  return Array.isArray(value) ? (value as string[]) : [];
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(initialLanguage);
  // Bumped when a lazily loaded catalog arrives, so t() re-renders with it
  const [catalogVersion, setCatalogVersion] = useState(0);

  useEffect(() => {
    if (i18n.hasResourceBundle(language, 'translation')) return;
    let active = true;
    i18n.loadLanguages(language).then(() => {
      if (active) setCatalogVersion((version) => version + 1);
    });
    return () => {
      active = false;
    };
  }, [language]);

  // Keep document <html lang dir> in sync
  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    if (!isEnabledLanguage(lang)) return;
    saveLanguage(lang);
    // t() passes the language explicitly, so a loaded catalog can be shown at once;
    // the others switch once their chunk has loaded
    const loaded = i18n.hasResourceBundle(lang, 'translation');
    if (loaded) setLanguageState(lang);
    i18n.changeLanguage(lang).finally(() => {
      if (!loaded) setLanguageState(lang);
    });
  }, []);

  const t = useCallback(
    (key: string, params?: TranslateParams): string => translate(language, key, params),
    // catalogVersion: a new t once a lazily loaded catalog is in
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language, catalogVersion],
  );

  const contextValue = useMemo<I18nContextType>(
    () => ({ language, setLanguage, t, languages: ENABLED_LANGUAGES }),
    [language, setLanguage, t],
  );

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};

/**
 * Like useTranslation, but works outside I18nProvider too (falls back to the saved or browser language).
 * For components that must render even when the providers failed, e.g. the outermost ErrorBoundary.
 */
export const useOptionalTranslation = (): Pick<I18nContextType, 't'> => {
  const context = useContext(I18nContext);
  if (context) {
    return context;
  }
  const language = readSavedLanguage() ?? browserLanguage();
  return { t: (key, params) => translate(language, key, params) };
};

// Shorthand hook for just the translation function
export const useT = () => {
  const { t } = useTranslation();
  return t;
};

// Helper function to translate backend error codes
export const translateBackendError = (
  t: (key: string, params?: { [key: string]: string | number }) => string,
  errorData: BackendErrorData | null | undefined,
  defaultMessage: string
): string => {
  if (!errorData) {
    return defaultMessage;
  }

  const { code, field, loginMode, details, error: errorText, message } = errorData;

  // If we have a specific error code, try to translate it
  if (code) {
    // Handle FIELD_REQUIRED with field-specific translations
    if (code === 'FIELD_REQUIRED' && field) {
      const fieldKey = loginMode && field === 'username' 
        ? `auth.backendErrors.FIELD_REQUIRED_${field}_login`
        : `auth.backendErrors.FIELD_REQUIRED_${field}`;
      const translation = t(fieldKey);
      if (translation !== fieldKey) {
        return translation;
      }
    }
    
    // Handle VALIDATION_FAILED with details
    if (code === 'VALIDATION_FAILED' && details && Array.isArray(details) && details.length > 0) {
      const firstError = details[0];
      if (firstError.code) {
        const translation = t(`auth.backendErrors.${firstError.code}`, { max: firstError.max ?? 1000 });
        if (translation !== `auth.backendErrors.${firstError.code}`) {
          return translation;
        }
      }
    }

    // Try direct code translation
    const translation = t(`auth.backendErrors.${code}`);
    if (translation !== `auth.backendErrors.${code}`) {
      return translation;
    }
  }

  // Fallback: if error text is an error code, try to translate
  if (errorText && typeof errorText === 'string') {
    const translation = t(`auth.backendErrors.${errorText}`);
    if (translation !== `auth.backendErrors.${errorText}`) {
      return translation;
    }
    // If it's not a code, return as-is (for backward compatibility)
    return errorText;
  }

  if (message) {
    return message;
  }

  return defaultMessage;
};
